import "components/shared/DetailTabs/style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, Tab } from "@mui/material";
import Edit from "assets/img/svg/edit";
import Plus from "assets/img/svg/plus";
import ImagePopup from "components/shared/ImagePopup";
import InformationGrid from "components/shared/InformationGrid";
import ResponsiveButton from "components/shared/ResponsiveStyledButton";
import TabCountLabel from "components/LoadingDots/TabCountLabel";
import TextSkeleton from "components/shared/TextSkeleton";
import { StyledTabs } from "components/shared/StyledTabs";
import Tags from "components/shared/Tags";
import { formatLocaleDateString, getInitials } from "helpers/utils";
import useDetailTabSwitch, { getDetailTabPanelContentKey } from "hooks/useDetailTabSwitch";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import { deleteTransfereeTag } from "store/transfereesStore";

import variables from "../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";
import TransfereeTermsTable from "./TransfereeTermsTable";

interface ITransfereePage {
    /**
     * Opens edit mode for TransfereeFormContainer.
     */
    openEditMode?: () => void;
    /**
     * Is edit mode open.
     */
    editMode?: boolean;
    termCount: number | null | undefined;
    isListLoading?: boolean;
}

enum ETransfereeTabs {
    INFO = "0",
    TERMS = "1",
}

/**
 * Transferee page with all its info.
 */
const Transferee = ({ openEditMode, editMode, termCount, isListLoading = false }: ITransfereePage) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { transferee: currentTransferee } = useAppSelector((state) => state.transferees);

    const { value, tabEnterKey, containerRef, handleTabChange } = useDetailTabSwitch(ETransfereeTabs.INFO);
    const [avatarPopupOpen, setAvatarPopupOpen] = useState(false);

    const routeTransfereeId = id ? +id : null;

    const personalInfos = {
        [t("Transferees.Email")]: currentTransferee?.email || "-",
        [t("Beneficiars.Siret")]: currentTransferee?.siretNumber || "-",
        [t("Transferees.IntraCommunityTVA")]: currentTransferee?.intraCommunityVat || "-",
    };

    const addressInfos = {
        [t("NewBeneficiary.StreetNumber")]: currentTransferee?.street || "-",
        [t("NewBeneficiary.ZIPCode")]: currentTransferee?.zipCode || "-",
        [t("NewBeneficiary.City")]: currentTransferee?.city || "-",
        [t("NewBeneficiary.Country")]: currentTransferee?.country || "-",
    };

    const pageInfos = {
        [t("Summary.CreationDate")]:
            formatLocaleDateString({ date: currentTransferee?.createdAt ?? new Date() }) || "-",
        [t("Titles.LatestEdition")]:
            formatLocaleDateString({ date: currentTransferee?.modifiedAt ?? new Date() }) || "-",
    };

    const handleDeleteTag = (tagId: number) => {
        dispatch(deleteTransfereeTag({ tagId }));
    };

    const isDetailLoading = isListLoading || (routeTransfereeId != null && currentTransferee?.id !== routeTransfereeId);

    return (
        <div
            ref={containerRef}
            className={styles["transferee-container"]}
            style={
                editMode
                    ? { overflow: "hidden", position: "relative", height: "100vh", width: "calc(100vw - 585px)" }
                    : {}
            }
        >
            {(currentTransferee?.id || isDetailLoading) && (
                <TabContext value={value}>
                    {currentTransferee?.id && !isDetailLoading && (
                        <ImagePopup
                            element={currentTransferee}
                            avatarOpen={avatarPopupOpen}
                            setAvatarOpen={() => setAvatarPopupOpen(false)}
                        />
                    )}
                    <div className={styles["transferee-header"]}>
                        <div className={styles["header-left-content"]}>
                            {isDetailLoading ? (
                                <TextSkeleton variant="circular" width={80} height={80} />
                            ) : (
                                <Avatar
                                    sx={{
                                        width: "80px",
                                        height: "80px",
                                        backgroundColor: !currentTransferee?.avatarUrl
                                            ? "rgba(184, 174, 233, 0.2)"
                                            : "",
                                        color: variables["foreground"],
                                        fontSize: "18px",
                                        cursor: currentTransferee?.avatarUrl !== "" ? "pointer" : "",
                                    }}
                                    variant="circular"
                                    src={currentTransferee?.avatarUrl}
                                    imgProps={{ sx: { objectFit: "cover" } }}
                                    onClick={() => currentTransferee.avatarUrl !== "" && setAvatarPopupOpen(true)}
                                >
                                    {!currentTransferee?.avatarUrl &&
                                        getInitials(currentTransferee?.name.toUpperCase() ?? "")}
                                </Avatar>
                            )}
                            <div className={styles["header-names"]}>
                                <p className={styles["header-eyebrow"]}>{t("Transferees.TransfereeLabel")}</p>
                                <h1>
                                    {isDetailLoading ? (
                                        <TextSkeleton width="12em" />
                                    ) : (
                                        currentTransferee?.name.toUpperCase() ?? ""
                                    )}
                                </h1>
                                {isDetailLoading ? (
                                    <TextSkeleton width="6em" />
                                ) : (
                                    <Tags
                                        elementId={currentTransferee?.id}
                                        elementType="transferees"
                                        onDelete={handleDeleteTag}
                                        tags={currentTransferee?.tags}
                                    />
                                )}
                            </div>
                        </div>
                        {!isDetailLoading && (
                            <div className={styles["header-buttons"]}>
                                <ResponsiveButton onClick={() => openEditMode && openEditMode()}>
                                    <Edit />
                                    <span>{t("Beneficiars.EditProfile")}</span>
                                </ResponsiveButton>
                                <ResponsiveButton
                                    onClick={() =>
                                        navigate("/transfer-terms/new", {
                                            state: { transfereeId: currentTransferee.id, from: "transfereePage" },
                                        })
                                    }
                                    buttonType="dark"
                                >
                                    <Plus />
                                    <span>
                                        {termCount && termCount > 0
                                            ? t("Beneficiars.AddAContract")
                                            : t("Beneficiars.CreateAContract")}
                                    </span>
                                </ResponsiveButton>
                            </div>
                        )}
                    </div>
                    <div className={styles["transferee-page-tabs"]}>
                        <StyledTabs value={value} onChange={handleTabChange}>
                            <Tab value={ETransfereeTabs.INFO} label={t("Beneficiars.GeneralInformation")} />
                            <Tab
                                value={ETransfereeTabs.TERMS}
                                label={
                                    <TabCountLabel
                                        text={t("Transferees.Terms")}
                                        count={termCount}
                                        isLoading={isDetailLoading || termCount == null}
                                    />
                                }
                            />
                        </StyledTabs>
                    </div>
                    <TabPanel value={ETransfereeTabs.INFO} className="detail-tab-panel-root">
                        <div
                            className="detail-tab-panel-content"
                            key={getDetailTabPanelContentKey(ETransfereeTabs.INFO, value, tabEnterKey)}
                        >
                            <div className="detail-tab-body">
                                <div className={styles["transferee-info-content"]}>
                                    <InformationGrid
                                        data={personalInfos}
                                        title={t("Transferees.Information")}
                                        isLoading={isDetailLoading}
                                    />
                                    <InformationGrid
                                        data={addressInfos}
                                        title={t("Beneficiars.Address")}
                                        isLoading={isDetailLoading}
                                    />
                                    <InformationGrid
                                        data={pageInfos}
                                        title={t("Titles.DateInfo")}
                                        isLoading={isDetailLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value={ETransfereeTabs.TERMS} className="detail-tab-panel-root">
                        <div
                            className="detail-tab-panel-content"
                            key={getDetailTabPanelContentKey(ETransfereeTabs.TERMS, value, tabEnterKey)}
                        >
                            <div className="detail-tab-body">
                                <TransfereeTermsTable
                                    transfereeId={currentTransferee?.id}
                                    isLoading={isDetailLoading}
                                />
                            </div>
                        </div>
                    </TabPanel>
                </TabContext>
            )}
        </div>
    );
};

export default Transferee;
